import {
  BookmarkRounded,
  EventRounded,
  FlashOnRounded,
  ForumRounded,
  LocalActivityRounded,
  SchoolRounded,
  SearchRounded,
  WifiTetheringRounded
} from '@mui/icons-material'
import { Box, Button, Dialog, DialogContent, ListItemButton, ListItemIcon, Skeleton, Tooltip, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import moment from 'moment'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import Emptyimage from '../../../../assets/Images/nofilter.png'
import { IRootState } from '../../../../redux/types'
import useScreenWidth from '../../hooks/useScreenWidth'
import ButtonNavbar from '../../styles/ButtonNavbar'
import HiddenDesktop from '../../styles/HiddenDesktop'
import HiddenMobile from '../../styles/HiddenMobile'
import InputGlobalSearch from './InputGlobalSearch'

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  '&.MuiListItemIcon-root': {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.common.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    height: 32,
    width: 32,
    maxWidth: 32,
    minWidth: 32,
    marginRight: 16
  }
}))

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: '0px 0px 16px',
  backgroundColor: theme.palette.common.white,
  marginTop: 8,
  borderRadius: 8,
  position: 'relative',
  '&::-webkit-scrollbar': {
    backgroundColor: theme.palette.grey[200],
    borderRadius: '4px',
    width: '8px',
    margin: '8px',
    '&-thumb': {
      backgroundColor: theme.palette.grey[500],
      borderRadius: '4px'
    }
  },
  minHeight: 350,
  maxHeight: 400,
  [theme.breakpoints.up('md')]: {
    minHeight: 450
  }
}))

const ContainerHero = styled('div')(({ theme }) => ({
  paddingLeft: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  backgroundColor: theme.palette.common.white,
  zIndex: 10,
  height: 24,
  paddingTop: 8,
  '& p': {
    fontWeight: 400
  },
  position: 'sticky',
  left: 0,
  right: 0,
  top: 0
}))

const SimulationInput = styled('div')(({ theme }) => ({
  display: 'flex',
  cursor: 'text',
  color: theme.palette.grey[400],
  backgroundColor: theme.palette.grey[100],
  width: 200,
  height: 24,
  alignItems: 'center',
  padding: 4,
  borderRadius: 4,
  '& p': {
    marginLeft: 8,
    fontWeight: 400
  },
  '&:hover': {
    color: theme.palette.grey[500],
    fontWeight: 500,
    '& svg': {
      color: `${theme.palette.primary.main}`
    }
  }
}))

const StyledTitleItem = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  maxWidth: 'calc(800px - 80px)',
  [theme.breakpoints.down('sm')]: {
    maxWidth: 'calc(100vw - 112px)'
  }
}))

function GlobalSearch() {
  const { t } = useTranslation(['global'])
  const navigate = useNavigate()
  const isMobile = useScreenWidth(960)

  const { globalSearch, loadingSearch } = useSelector((state: IRootState) => state.global)

  const [open, setOpen] = useState(false)

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  function redirectHit(type: string, path: string, data: any) {
    handleClose()
    if ('course' === type) {
      navigate(`/${path}/${data.brand_id}/${data.slug}`)
    } else if ('novelty' === type) {
      navigate(`/${path}/${data.slug}`)
    } else {
      navigate(`/${path}/${data.id}`)
    }
  }

  function ResourceHit({ hit }: any) {
    return (
      <ListItemButton
        onClick={() => {
          redirectHit('resource', 'biblioteca', hit)
        }}
      >
        <StyledListItemIcon>
          <BookmarkRounded />
        </StyledListItemIcon>
        <Box>
          <Tooltip title={hit.alias === null ? hit.file_name : hit.alias} arrow>
            <StyledTitleItem>{hit.alias === null ? hit.file_name : hit.alias}</StyledTitleItem>
          </Tooltip>
          <Box
            display="flex"
            gap="8px"
            alignItems="center"
            sx={theme => ({
              '& p, span': {
                lineHeight: '14px',
                color: theme.palette.grey[500]
              }
            })}
          >
            <Typography variant="caption">{hit.description}</Typography>
            <Typography>•</Typography>
            <HiddenMobile>
              <Typography variant="caption">{`${moment(hit.created_at).format('LL')}`}</Typography>
            </HiddenMobile>
          </Box>
        </Box>
      </ListItemButton>
    )
  }

  function CourseHit({ hit }: any) {
    return (
      <ListItemButton
        onClick={() => {
          redirectHit('course', 'cursos', hit)
        }}
      >
        <StyledListItemIcon>
          <SchoolRounded />
        </StyledListItemIcon>
        <Box>
          <Tooltip title={hit.title} arrow>
            <StyledTitleItem>{hit.title}</StyledTitleItem>
          </Tooltip>
          <Box
            display="flex"
            gap="8px"
            alignItems="center"
            sx={theme => ({
              '& p, span': {
                lineHeight: '14px',
                color: theme.palette.grey[500]
              }
            })}
          >
            <Typography variant="caption">{hit.title}</Typography>
            <Typography>•</Typography>
            <HiddenMobile>
              <Typography variant="caption">{`${moment(hit.created_at).format('LL')}`}</Typography>
            </HiddenMobile>
          </Box>
        </Box>
      </ListItemButton>
    )
  }

  function EventHit({ hit }: any) {
    return (
      <ListItemButton
        onClick={() => {
          redirectHit('event', 'eventos', hit)
        }}
      >
        <StyledListItemIcon>
          <EventRounded />
        </StyledListItemIcon>
        <Box>
          <Tooltip title={hit.title} arrow>
            <StyledTitleItem>{hit.title}</StyledTitleItem>
          </Tooltip>
          <Box
            display="flex"
            gap="8px"
            alignItems="center"
            sx={theme => ({
              '& p, span': {
                lineHeight: '14px',
                color: theme.palette.grey[500]
              }
            })}
          >
            <Typography variant="caption">{hit.title}</Typography>
            <Typography>•</Typography>
            <HiddenMobile>
              <Typography variant="caption">{`${moment(hit.created_at).format('LL')}`}</Typography>
            </HiddenMobile>
          </Box>
        </Box>
      </ListItemButton>
    )
  }

  function NoveltyHit({ hit }: any) {
    return (
      <ListItemButton
        onClick={() => {
          hit.type === 0 ? redirectHit('novelty', 'novedades', hit) : redirectHit('news', 'noticias', hit)
        }}
      >
        <StyledListItemIcon>
          {hit.type === 0 && <FlashOnRounded />}
          {hit.type === 2 && <ForumRounded />}
        </StyledListItemIcon>
        <Box>
          <Tooltip title={hit.title} arrow>
            <StyledTitleItem>{hit.title}</StyledTitleItem>
          </Tooltip>
          <Box
            display="flex"
            gap="8px"
            alignItems="center"
            sx={theme => ({
              '& p, span': {
                lineHeight: '14px',
                color: theme.palette.grey[500]
              }
            })}
          >
            <HiddenMobile>
              <Typography variant="caption">{`${moment(hit.created_at).format('LL')}`}</Typography>
            </HiddenMobile>
          </Box>
        </Box>
      </ListItemButton>
    )
  }

  function OfferHit({ hit }: any) {
    return (
      <ListItemButton
        onClick={() => {
          redirectHit('offer', 'ofertas', hit)
        }}
      >
        <StyledListItemIcon>
          <LocalActivityRounded />
        </StyledListItemIcon>
        <Box>
          <Tooltip title={hit.title} arrow>
            <StyledTitleItem>{hit.title}</StyledTitleItem>
          </Tooltip>
          <Box
            display="flex"
            gap="8px"
            alignItems="center"
            sx={theme => ({
              '& p, span': {
                lineHeight: '14px',
                color: theme.palette.grey[500]
              }
            })}
          >
            <Typography variant="caption">{hit.description}</Typography>
            <Typography>•</Typography>
            <HiddenMobile>
              <Typography variant="caption">{`${moment(hit.created_at).format('LL')}`}</Typography>
            </HiddenMobile>
          </Box>
        </Box>
      </ListItemButton>
    )
  }

  function SeminarHit({ hit }: any) {
    return (
      <ListItemButton
        onClick={() => {
          redirectHit('seminar', 'seminarios', hit)
        }}
      >
        <StyledListItemIcon>
          <WifiTetheringRounded />
        </StyledListItemIcon>
        <Box>
          <Tooltip title={hit.title} arrow>
            <StyledTitleItem>{hit.title}</StyledTitleItem>
          </Tooltip>
          <Box
            display="flex"
            gap="8px"
            alignItems="center"
            sx={theme => ({
              '& p, span': {
                lineHeight: '14px',
                color: theme.palette.grey[500]
              }
            })}
          >
            <Typography variant="caption">{hit.description}</Typography>
            <Typography>•</Typography>
            <HiddenMobile>
              <Typography variant="caption">{`${moment(hit.created_at).format('LL')}`}</Typography>
            </HiddenMobile>
          </Box>
        </Box>
      </ListItemButton>
    )
  }

  function SkeletonListItem() {
    return (
      <Box display="flex" alignItems="center" pt={1} pb={1} px={2} width="100%">
        <Skeleton animation="wave" variant="rectangular" height={32} width={32} style={{ borderRadius: 4 }} />
        <Box ml={2} display="flex" flexDirection="column" width="100%">
          <Skeleton animation="wave" variant="text" width="70%" height={24} />
          <Skeleton animation="wave" variant="text" width={150} />
        </Box>
      </Box>
    )
  }

  return (
    <>
      <HiddenMobile>
        <Tooltip title={`${t('search')}`}>
          <Button onClick={handleOpen} disableElevation disableFocusRipple disableTouchRipple disableRipple sx={{ padding: 0 }}>
            <SimulationInput>
              <SearchRounded />
              <Typography>{`${t('searchIn')} ${import.meta.env.VITE_NAME}`}</Typography>
            </SimulationInput>
          </Button>
        </Tooltip>
      </HiddenMobile>

      <HiddenDesktop>
        <ButtonNavbar onClick={handleOpen}>
          <SearchRounded />
        </ButtonNavbar>
      </HiddenDesktop>

      <Dialog
        open={open}
        fullWidth={true}
        fullScreen={isMobile && true}
        maxWidth={!isMobile && 'md'}
        onClose={handleClose}
        aria-labelledby="search-dialog"
        aria-describedby="search-dialog-description"
        sx={theme => ({
          '&.MuiDialog-paper': {
            bgcolor: 'background.transparent',
            boxShadow: 'none',
            overflow: 'hidden',
            [theme.breakpoints.down('sm')]: {
              margin: 16
            }
          }
        })}
      >
        <InputGlobalSearch handleClose={handleClose} />
        <StyledDialogContent style={loadingSearch ? { overflow: 'hidden' } : {}}>
          <ContainerHero>
            <Typography variant="body2" color="textSecondary">
              {t('search-results')}
            </Typography>
          </ContainerHero>

          {loadingSearch ? (
            <>
              <SkeletonListItem />
              <SkeletonListItem />
              <SkeletonListItem />
              <SkeletonListItem />
              <SkeletonListItem />
              <SkeletonListItem />
              <SkeletonListItem />
            </>
          ) : (
            <>
              {globalSearch.count === 0 && (
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Box my={2}>
                    <img src={Emptyimage} width={isMobile ? '200px' : '250px'} alt="logo" />
                  </Box>
                  <>
                    <Typography variant="subtitle1" align="center">
                      {t('search-empty-text-hero')}
                    </Typography>
                    <Typography>{t('search-empty-text-caption')}</Typography>
                  </>
                </Box>
              )}
              {globalSearch.resources.map((resource: any) => (
                <ResourceHit key={resource.id} hit={resource} />
              ))}

              {globalSearch.courses.map((course: any) => (
                <CourseHit key={course.id} hit={course} />
              ))}

              {globalSearch.events.map((event: any) => (
                <EventHit key={event.id} hit={event} />
              ))}

              {globalSearch.novelties.map((novelty: any) => (
                <NoveltyHit key={novelty.id} hit={novelty} />
              ))}

              {globalSearch.offers.map((offer: any) => (
                <OfferHit key={offer.id} hit={offer} />
              ))}

              {globalSearch.seminars.map((seminar: any) => (
                <SeminarHit key={seminar.id} hit={seminar} />
              ))}
            </>
          )}
        </StyledDialogContent>
      </Dialog>
    </>
  )
}

export default GlobalSearch
